import { useFormContext } from 'react-hook-form/dist/index.ie11';
import {
  defaultDayTags,
  IMealCreditFormFields,
  MealCreditLength,
  Meridian,
  MONTHLY_DEFAULT_BUDGET_RESET_PERIOD,
  RadioOption,
  RadioOptionExtended,
  setTimeToDate,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import {
  ExpiresOn,
  MonthAvailability,
  TimeIntervalTypeValues,
} from 'src/components/oneCorp/features/MealCredit/common/formFields/mealCreditFormFields.utils';
import { BudgetResetTypeValues } from 'src/components/oneCorp/features/MealCredit/common/formFields/Select/BudgetResetTypeSelect/BudgetResetTypeSelect.utils';
import { getBudgetLengthDetails } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getBudgetLengthDetails';
import { getMonthAvailability } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getMonthAvailability';
import { getDailyPeriodEndFromStartDay } from 'src/components/oneCorp/features/MealCredit/utils/getDailyPeriodEndFromStartDay';

export interface UseFormWatcher {
  budgetLength: MealCreditLength;
  selectedDays: boolean[];
  timeIntervalType: TimeIntervalTypeValues;
  budgetResetPeriod: string;
  startTime: string;
  startTimeMeridian: Meridian;
  endTime: string;
  endTimeMeridian: Meridian;
  date: Date;
  startDate: Date | null;
  endTimeFullDate: Date | null;
  orderPolicyAcceptanceRequired: RadioOption;
  displayBudgets: RadioOption;
  expenseCodes: RadioOptionExtended;
  expenseComments: RadioOptionExtended;
  expenseReason: RadioOption;
  sendLocWelcomeEmail: RadioOption;
  isDailyLengthBudget: boolean;
  isWeeklyLengthBudget: boolean;
  isMonthlyLengthBudget: boolean;
  isCustomLengthBudget: boolean;
  isMultiDaysLengthBudget: boolean;
  isRepeating: boolean;
  hasLocation: boolean;
  isTimeInputDisabled: boolean;
  expirationDate: Date | null;
  expiresOn: ExpiresOn;
  budgetResetType: BudgetResetTypeValues;
  numberOfConsecutiveDays: number;
  numberOfConsecutiveMonths: number;
  isMonthAvailable: boolean;
  isMonthAvailableOnSpecificDay: boolean;
  isMonthAvailableEveryDay: boolean;
  isMonthAvailableWithinDateRange: boolean;
  isDaysAvailable: boolean;
  monthAvailableOn: MonthAvailability;
  monthAvailableTo: Date;
}

export const useFormWatcher = ({
  isEditingMealCredit,
}: {
  isEditingMealCredit: boolean;
}): UseFormWatcher => {
  const { watch, errors } = useFormContext<IMealCreditFormFields>();

  const {
    budgetLength,
    frequency,
    days: selectedDays = defaultDayTags,
    timeIntervalType = TimeIntervalTypeValues.ALL_DAY,
    budgetResetPeriod,
    date,
    startTime,
    endTime,
    expiration,
    startTimeMeridian,
    endTimeMeridian,
    locationsExistence,
    orderPolicyAcceptanceRequired,
    displayBudgets,
    expenseCodes,
    expenseComments,
    expenseReason,
    sendLocWelcomeEmail,
    expiresOn,
    budgetResetType,
    numberOfConsecutiveDays = 1,
    numberOfConsecutiveMonths = MONTHLY_DEFAULT_BUDGET_RESET_PERIOD,
    monthAvailableOn,
    monthAvailableTo: monthAvailableToDate,
  } = watch([
    'budgetLength',
    'frequency',
    'days',
    'timeIntervalType',
    'budgetResetPeriod',
    'date',
    'startTime',
    'endTime',
    'expiration',
    'startTimeMeridian',
    'endTimeMeridian',
    'locationsExistence',
    'orderPolicyAcceptanceRequired',
    'displayBudgets',
    'expenseCodes',
    'expenseComments',
    'expenseReason',
    'sendLocWelcomeEmail',
    'expiresOn',
    'numberOfConsecutiveDays',
    'numberOfConsecutiveMonths',
    'budgetResetType',
    'monthAvailableOn',
    'monthAvailableTo',
  ]);

  const {
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMonthlyLengthBudget,
    isCustomLengthBudget,
    isMultiDaysLengthBudget,
    isMonthAvailable,
  } = getBudgetLengthDetails(budgetLength, budgetResetType);

  const {
    isMonthAvailableOnSpecificDay,
    isMonthAvailableEveryDay,
    isMonthAvailableWithinDateRange,
  } = getMonthAvailability(isMonthAvailable, monthAvailableOn);

  const isRepeating = frequency === 'repeats';
  const hasLocation = locationsExistence === 'hasLocation';
  const isTimeInputDisabled =
    timeIntervalType === TimeIntervalTypeValues.ALL_DAY || isEditingMealCredit;
  const isDaysAvailable = isMultiDaysLengthBudget
    ? !isMonthAvailableOnSpecificDay
    : isRepeating;

  const startDate = setTimeToDate(
    date,
    errors.startTime ? '12:00' : startTime,
    startTimeMeridian
  );

  const endTimeFullDate = setTimeToDate(
    getDailyPeriodEndFromStartDay(date, timeIntervalType),
    errors.endTime ? '12:00' : endTime,
    endTimeMeridian
  );

  const monthAvailableTo = setTimeToDate(
    getDailyPeriodEndFromStartDay(
      isMonthAvailableOnSpecificDay ? date : monthAvailableToDate,
      timeIntervalType
    ),
    errors.endTime ? '12:00' : endTime,
    endTimeMeridian
  );

  const isExpirationDateValid = isRepeating && expiration && !errors.expiration;
  const expirationDate = isExpirationDateValid
    ? setTimeToDate(
        getDailyPeriodEndFromStartDay(expiration, timeIntervalType),
        errors.endTime ? '12:00' : endTime,
        endTimeMeridian
      )
    : null;

  return {
    budgetLength,
    selectedDays,
    timeIntervalType,
    budgetResetPeriod,
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
    date,
    startDate,
    endTimeFullDate,
    orderPolicyAcceptanceRequired,
    displayBudgets,
    expenseCodes,
    expenseComments,
    expenseReason,
    sendLocWelcomeEmail,
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMonthlyLengthBudget,
    isCustomLengthBudget,
    isMultiDaysLengthBudget,
    isRepeating,
    hasLocation,
    isTimeInputDisabled,
    expirationDate,
    expiresOn,
    budgetResetType,
    numberOfConsecutiveDays,
    numberOfConsecutiveMonths,
    isMonthAvailable,
    isMonthAvailableOnSpecificDay,
    isMonthAvailableEveryDay,
    isMonthAvailableWithinDateRange,
    isDaysAvailable,
    monthAvailableOn,
    monthAvailableTo,
  };
};
